/**
 * Utilitários para exportação de dados em diferentes formatos
 */
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from "xlsx";
import { translateSubscriptionStatus } from "./subscriptionStatus";

/**
 * Converte dados para formato CSV e inicia o download
 * @param data Array de objetos para converter
 * @param fileName Nome do arquivo a ser baixado
 */
export const exportToCSV = (data: any[], fileName: string) => {
  if (!data || data.length === 0) {
    console.error("Nenhum dado para exportar");
    return;
  }

  // Obtém todas as chaves únicas de todos os objetos
  const keys = Array.from(
    new Set(
      data.reduce((acc: string[], item: any) => {
        return [...acc, ...Object.keys(item)];
      }, [])
    )
  );

  // Cria o cabeçalho do CSV
  let csvContent = keys.join(",") + "\n";

  // Adiciona as linhas de dados
  csvContent += data
    .map((row) => {
      return keys
        .map((key) => {
          // Lida com valores que podem conter vírgulas ou aspas (escapa esses valores)
          const cell = row[key] === null || row[key] === undefined ? "" : row[key];
          const cellStr = String(cell);

          // Se contém vírgulas, aspas ou quebras de linha, coloca entre aspas e escapa aspas internas
          if (cellStr.includes(",") || cellStr.includes("\"") || cellStr.includes("\n")) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(",");
    })
    .join("\n");

  // Cria um blob com o conteúdo CSV
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  // Cria um link para download
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${fileName}.csv`);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Converte dados para formato XLS e inicia o download
 * @param data Array de objetos para converter
 * @param fileName Nome do arquivo a ser baixado
 */
export const exportToXLS = (data: any[], fileName: string) => {
  if (!data || data.length === 0) {
    console.error("Nenhum dado para exportar");
    return;
  }

  // 1) Unir todas as chaves para garantir cabeçalho completo e ordem estável
  const headers = Array.from(
    new Set(
      data.reduce((acc: string[], item: any) => acc.concat(Object.keys(item || {})), [])
    )
  );

  // 2) Construir matriz (AoA) com cabeçalhos + linhas
  const aoa: any[][] = [headers];
  for (const row of data) {
    aoa.push(headers.map((h) => {
      const v = row?.[h];
      // Normaliza objetos/arrays para string legível
      if (v === null || v === undefined) return "";
      if (typeof v === "object") return JSON.stringify(v);
      return v;
    }));
  }

  // 3) Criar worksheet e workbook reais de Excel
  const ws = XLSXUtils.aoa_to_sheet(aoa);

  // Ajuste opcional de largura de colunas baseado no maior conteúdo
  const colWidths = headers.map((h, colIdx) => {
    let maxLen = String(h ?? "").length;
    for (let r = 1; r < aoa.length; r++) {
      const cellVal = aoa[r][colIdx];
      const len = String(cellVal ?? "").length;
      if (len > maxLen) maxLen = len;
    }
    // Converter caracteres para largura aproximada em Excel (mínimo 8)
    return { wch: Math.max(8, Math.min(60, Math.ceil(maxLen * 1.1))) };
  });
  (ws as any)['!cols'] = colWidths;

  const wb = XLSXUtils.book_new();
  XLSXUtils.book_append_sheet(wb, ws, 'Dados');

  // 4) Baixar como .xlsx (Excel real e moderno, multiplataforma)
  // Se precisar especificamente do legado .xls, podemos usar { bookType: 'xls' }.
  XLSXWriteFile(wb, `${fileName}.xlsx`, { bookType: 'xlsx' });
};

// Função escapeHtml foi removida pois mudamos para abordagem CSV

/**
 * Formata dados de vendas para exportação, simplificando a estrutura aninhada
 * @param sales Array de vendas com estrutura complexa
 * @returns Array de objetos simplificados para exportação
 */
/**
 * Formata ASSINATURAS para exportação.
 *
 * Antes a tela de assinaturas chamava formatSalesForExport, que espera a forma de
 * uma VENDA (sale.date, sale.payment.grossAmount, sale.tracking, sale.item...).
 * A linha de assinatura tem outra forma, então quase toda coluna saía "-" e as
 * colunas que importam (plano, ciclo, próxima cobrança) não existiam.
 *
 * Forma devolvida por GET /api/subscriptions → subscriptionsList:
 *   { id, code, date, nextCycleDate, currentCycle, status,
 *     customer: { name, email, document, phone },
 *     plan: { name, frequency, price, totalCycles, product: { name } },
 *     lastBilling: { status, originalAmount, chargedAmount, createdAt } | null,
 *     payment:     { method, status, grossAmount, netAmount, installments, paidAt } | null }
 */
/** Linha de assinatura devolvida por GET /api/subscriptions → subscriptionsList. */
export interface SubscriptionExportRow {
  id?: string;
  code?: string;
  date?: string | null;
  nextCycleDate?: string | null;
  currentCycle?: number;
  status?: string;
  customer?: { name?: string; email?: string; document?: string; phone?: string };
  plan?: {
    name?: string;
    frequency?: string;
    price?: number;
    totalCycles?: number | null;
    product?: { name?: string };
  };
  lastBilling?: { status?: string; chargedAmount?: number; createdAt?: string | null } | null;
  payment?: {
    method?: string;
    grossAmount?: number;
    netAmount?: number;
    installments?: number;
  } | null;
}

export const formatSubscriptionsForExport = (subscriptions: SubscriptionExportRow[]) => {
  return subscriptions.map(sub => ({
    "Código": sub.code || sub.id || "-",
    "Cliente": sub.customer?.name || "-",
    "Email": sub.customer?.email || "-",
    "Documento": sub.customer?.document || "-",
    "Telefone": sub.customer?.phone || "-",
    "Produto": sub.plan?.product?.name || "-",
    "Plano": sub.plan?.name || "-",
    "Frequência": translateFrequency(sub.plan?.frequency),
    "Valor do Plano": formatCurrencyValue(sub.plan?.price),
    "Status": translateSubscriptionStatus(sub.status),
    "Ciclo Atual": sub.currentCycle ?? 0,
    "Total de Ciclos": sub.plan?.totalCycles ? sub.plan.totalCycles : "Sem limite",
    "Data de Início": formatDateValue(sub.date),
    "Próxima Cobrança": formatDateValue(sub.nextCycleDate),
    "Método de Pagamento": translatePaymentMethod(sub.payment?.method),
    "Parcelas": sub.payment?.installments ?? "-",
    "Última Cobrança": formatDateValue(sub.lastBilling?.createdAt),
    "Status da Última Cobrança": translateBillingStatus(sub.lastBilling?.status),
    "Valor Cobrado": formatCurrencyValue(sub.lastBilling?.chargedAmount),
    "Valor Bruto": formatCurrencyValue(sub.payment?.grossAmount),
    "Valor Líquido": formatCurrencyValue(sub.payment?.netAmount),
  }));
};

/**
 * Status da cobrança (tabela `billing`) em português.
 * Separado do translateStatus de vendas, que só conhece status de pagamento e
 * devolveria undefined para 'processed'/'cancelled'.
 */
const translateBillingStatus = (status?: string | null): string => {
  const mapa: Record<string, string> = {
    processed: "Paga",
    paid: "Paga",
    approved: "Paga",
    pending: "Pendente",
    failed: "Falhou",
    declined: "Recusada",
    refused: "Recusada",
    // o job encerra o billing como 'cancelled' quando desiste de cobrar
    cancelled: "Encerrada sem pagamento",
    canceled: "Encerrada sem pagamento",
    refunded: "Reembolsada",
    chargeback: "Chargeback",
  };
  const s = String(status ?? "").toLowerCase();
  if (!s) return "-";
  return mapa[s] ?? String(status);
};

/** Frequência do plano em português. */
const translateFrequency = (frequency?: string | null): string => {
  const mapa: Record<string, string> = {
    weekly: "Semanal",
    monthly: "Mensal",
    quarterly: "Trimestral",
    semiannual: "Semestral",
    annual: "Anual",
    yearly: "Anual",
  };
  const f = String(frequency ?? "").toLowerCase();
  return mapa[f] ?? (frequency ? String(frequency) : "-");
};

/** Método de pagamento em português. */
const translatePaymentMethod = (method?: string | null): string => {
  const mapa: Record<string, string> = {
    card: "Cartão",
    credit_card: "Cartão",
    pix: "PIX",
    boleto: "Boleto",
  };
  const m = String(method ?? "").toLowerCase();
  return mapa[m] ?? (method ? String(method) : "-");
};

export const formatSalesForExport = (sales: any[]) => {
  return sales.map(sale => {
    // Extrair os dados de UTM do objeto tracking
    const tracking = sale.tracking || {};

    // Formatar os dados para exportação, achatando estrutura aninhada
    return {
      "ID": sale.id,
      "Data Transação": formatDateValue(sale.date),
      "Hora da Transação": formatTimeValue(sale.date),
      "Data Pagamento": formatDateValue(sale.paidAt),
      "Hora do Pagamento": formatTimeValue(sale.paidAt),
      "Cliente": sale.customer?.name || "-",
      "Documento": sale.customer?.document || "-",
      "Telefone": sale.customer?.phone || "-",
      "Email": sale.customer?.email || "-",
      "Produto": sale.item?.productName || "-",
      "Oferta": sale.item?.offerName || "-",
      "Valor Bruto": formatCurrencyValue(sale.payment?.grossAmount),
      "Valor Líquido": formatCurrencyValue(sale.payment?.netAmount),
      "Taxa": formatCurrencyValue(sale.payment?.platformFee),
      "Método": sale.payment?.method || "-",
      "Parcelas": sale.order?.installments,
      "Status": translateStatus(sale.payment?.status),
      "Data de Liberação Estimada": formatDateValue(sale.payment?.estimatedReleaseDate),
      // Campos de UTM em colunas separadas
      "SCK": formatUtmValue(tracking.sck || "-"),
      "UTM Source": formatUtmValue(tracking.utm_source || "-"),
      "UTM Medium": formatUtmValue(tracking.utm_medium || "-"),
      "UTM Campaign": formatUtmValue(tracking.utm_campaign || "-"),
      "UTM Content": formatUtmValue(tracking.utm_content || "-"),
      "UTM Term": formatUtmValue(tracking.utm_term || "-"),
    };
  });
};

/**
 * Formata um valor de data
 */
const formatDateValue = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString("pt-BR");
  } catch {
    return "-";
  }
};

/**
 * Formata um valor de hora
 */
const formatTimeValue = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleTimeString("pt-BR");
  } catch {
    return "-";
  }
};

/**
 * Formata um valor monetário
 */
const formatCurrencyValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "R$ 0,00";
  return `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
};

/**
 * Formata um valor de UTM (decodificando-o se necessário)
 */
const formatUtmValue = (value: string | null | undefined): string => {
  if (!value) return "";
  // Decodifica o valor se contiver caracteres codificados como '+'  
  try {
    // Substitui '+' por espaços e decodifica caracteres URL encoded
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    // Em caso de erro na decodificação, retorna o valor original
    return value;
  }
};

/**
 * Traduz status de pagamento para português
 */
const translateStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    approved: "Paga",
    pending: "Pendente",
    declined: "Recusada",
    refunded: "Reembolsada",
    chargeback: "Chargeback",
  };

  return statusMap[status] || status;
};
